import { useEffect, useState } from "react";
import { fetchNotificationSettings } from "@/lib/api";
import { Check, Circle } from "lucide-react";

export default function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNotificationSettings()
      .then(setSettings)
      .catch((e) => setError(e?.message || "Failed to load settings"));
  }, []);

  return (
    <div data-testid="settings-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-3xl px-6 md:px-10">
        <p className="label-eyebrow">Admin · House Operations</p>
        <h1 className="font-display text-bone text-5xl md:text-6xl mt-5 leading-[0.95]">Notification Settings.</h1>
        <p className="mt-6 text-bone/65 max-w-xl">
          Where The House sends every form notification. The destination inbox and the email provider are
          configured via the backend <code className="text-brass">.env</code> file — they are read-only here
          for security.
        </p>

        {error && (
          <p data-testid="settings-error" className="mt-6 text-red-300/85 text-sm">{error}</p>
        )}

        {settings && (
          <div className="mt-10 space-y-6">
            <Row label="Destination Inbox">
              <p data-testid="settings-email" className="font-display text-bone text-2xl">
                {settings.notification_email || <span className="text-bone/50">— not set —</span>}
              </p>
            </Row>
            <Row label="Email Provider (Resend)">
              <Status ok={settings.email_provider_configured} testId="settings-email-provider"
                okText="Configured & live" pendingText="Not configured — emails will log to backend only" />
            </Row>
            <Row label="SMS Provider (Twilio)">
              <Status ok={settings.sms_provider_configured} testId="settings-sms-provider"
                okText="Configured & live" pendingText="Not configured — welcome SMS will log only" />
            </Row>
          </div>
        )}

        <div className="mt-14 border border-bone/10 p-6 md:p-8 text-bone/75 text-sm leading-relaxed">
          <p className="label-eyebrow">How to wire it up</p>
          <ol className="mt-4 list-decimal pl-5 space-y-2">
            <li>
              Set <code className="text-brass">NOTIFICATION_EMAIL</code> in <code>backend/.env</code> to the
              inbox that should receive every submission alert.
            </li>
            <li>
              Create a verified sending domain in{" "}
              <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-brass hover:text-champagne">Resend</a>,
              then set <code className="text-brass">RESEND_API_KEY</code> and{" "}
              <code className="text-brass">RESEND_FROM</code> (e.g. <em>The Uncanny House &lt;no-reply@theuncannyhouse.com&gt;</em>).
            </li>
            <li>
              Restart the backend (<code>sudo supervisorctl restart backend</code>) and reload this page —
              both providers will flip to <em>Configured &amp; live</em>.
            </li>
          </ol>
          <p className="mt-4 text-bone/55 text-xs">
            Until Resend is configured, every submission is still stored in the database and the
            outbound email is written to the backend logs with the full payload so nothing is ever lost.
          </p>
        </div>
      </section>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="border border-bone/10 p-5 md:p-6">
      <p className="label-eyebrow text-bone/55">{label}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Status({ ok, okText, pendingText, testId }) {
  return (
    <p data-testid={testId} className="flex items-center gap-3 text-bone">
      {ok ? <Check size={16} className="text-brass" /> : <Circle size={14} className="text-bone/40" />}
      <span className={ok ? "text-bone" : "text-bone/60"}>{ok ? okText : pendingText}</span>
    </p>
  );
}
